import { z } from "zod";
/** An id Finago generated in one company; it differs between the test company and production. */
const finagoIdSchema = z.number().int().positive();
/**
 * `config/finago`, one per environment. Only what Finago generates per company is configured;
 * accounts, tax codes and the like are fixed in code until one turns out not to be.
 */
export const finagoConfigSchema = z.object({
    /** Finago's internal product id per karat ("Kjøp brukt gull <n>K"), as in the product's URL. */
    productIds: z.object({
        "9": finagoIdSchema,
        "14": finagoIdSchema,
        "18": finagoIdSchema,
        "22": finagoIdSchema,
        "24": finagoIdSchema,
    }),
    /**
     * The stone-removal product, booked to its own income account (Morten, 2026-10-09). Leaving it
     * out switches the deduction line off; it stays out until an accountant has said how it is booked.
     */
    stoneRemovalProductId: finagoIdSchema.optional(),
    /**
     * "Vår referanse": always where the gold is analysed, Service Egertorget (owner, 2026-10-15).
     * Finago's id for that person; prod's is 113297.
     */
    ourReferenceId: finagoIdSchema,
});
/**
 * What is stored, as far as it goes. A field added to the config later is missing from a document
 * saved before it, and the Finago page must still show the rest so it can be filled in; only an
 * invoice needs the whole `finagoConfigSchema`.
 */
const storedFinagoConfigSchema = finagoConfigSchema
    .extend({ productIds: finagoConfigSchema.shape.productIds.partial() })
    .partial();
/** The Finago integration page: the stored config, and whether the credentials are in place. */
export const finagoSettingsResponseSchema = z.object({
    /** Null until a system admin has saved one for this environment. */
    config: storedFinagoConfigSchema.nullable(),
    /** The client id, client secret and organization id are all present in this environment. */
    credentialsConfigured: z.boolean(),
});
/** "Lag faktura": the outbox task that will build the invoice. */
export const createFinagoInvoiceResponseSchema = z.object({
    taskId: z.string(),
});
/** "Bokfør": Finago's id for the draft posting (bunt) it made. */
export const createFinagoPostingResponseSchema = z.object({
    finagoPostingDraftId: z.string(),
});
/**
 * Why the invoice worker declined to make an invoice. These are written on the transaction rather
 * than retried, because retrying cannot change them: someone has to fix the config or the numbers.
 */
export const finagoInvoiceErrorSchema = z.enum([
    /** No complete `config/finago`, or a karat on the delivery has no product id. */
    "FINAGO_NOT_CONFIGURED",
    /** The transaction has no linked delivery with priced karat rows to invoice. */
    "NO_ASSESSMENT_ROWS",
    /** The lines do not add up to what is paid out: a deduction while the switch is off, or an override. */
    "INVOICE_TOTAL_MISMATCH",
    /** There is no account to put on the Finago customer. */
    "BANK_ACCOUNT_MISSING",
    /**
     * The store (or, for post, the warehouse) has no VisionPoint interaction customer number, which
     * is also its department id in Finago.
     */
    "DEPARTMENT_NOT_CONFIGURED",
]);
